import React, { useEffect, useContext, useMemo } from 'react';
import { string, func, arrayOf } from 'prop-types';
import { ExperienceContext, useStoreId } from '~/@thd-nucleus/experience-context';
import { useProductAddOnsFlow } from '../hooks/useProductAddOnsFlow';
import { HomeDepotProtectionPlan } from './add-ons/hdpp/HomeDepotProtectionPlan';
import { AttachLabor } from './add-ons/attach-labor/AttachLabor';
import { ProReferral } from './add-ons/pro-referral/ProReferral';
import { DualPath } from './add-ons/dual-path/DualPath';
import { SAMDualPath } from './add-ons/SAM-dual-path/SAMDualPath';
import { displayAttachLabor as attachLaborEligible } from '../utils/product-add-on-utils';
import { dataModel } from './dataModel';
import './product-add-ons.scss';
import './tooltip.scss';

const ProductAddOns = ({
  itemId, fulfillment, availableFulfillments, onChange
}) => {

  const expData = useContext(ExperienceContext);
  const { channel } = expData;

  const storeId = useStoreId();

  const {
    loading,
    data,
    isProtectionPlan,
    isProReferral,
    isAssembly,
    isDualPath,
    isInstallationSku,
    inStoreAssemblyAvailable,
    isSAMDualPath
  } = useProductAddOnsFlow({ itemId, storeId });

  const getCategory = (serviceName) => {
    const PR_INSTALL = 'PRInstall';
    const PAINTER = 'Painter';
    const ASSEMBLY = 'Assembly';

    if (serviceName === 'interior_painting' || serviceName === 'exterior_painting') return PAINTER;
    if (serviceName === 'barbeque_assembly' || serviceName === 'outdoor_furniture_assembly') return ASSEMBLY;

    return PR_INSTALL;
  };

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('product-add-ons.ready'); }, []);

  const { installProcess, priceIncludes } = useMemo(() => {
    const doItForMe = data?.serviceAddOns?.attachLabor?.installSkus?.[0]?.doItForMe;
    return {
      priceIncludes: (doItForMe?.priceIncludes || []).filter((priceInclude) => priceInclude.shortText),
      installProcess: (doItForMe?.installProcess || []).filter((installProcessItem) => installProcessItem.longText)
    };
  }, [data?.serviceAddOns?.attachLabor?.installSkus?.[0]?.doItForMe]);

  if (!data || loading) {
    return null;
  }

  if (!data?.protectionPlan && !data?.serviceAddOns && !isSAMDualPath) {
    return null;
  }

  const { price, termLength, detailsUrl, itemId: protectionPlanItemId } = data?.protectionPlan || {};

  const { installationProgramCategory } = data?.serviceAddOns?.sellFurnishInstall || {};
  const { serviceName } = data?.serviceAddOns?.proReferrals?.[0] || {};
  const { attachLabor } = data?.serviceAddOns || {};
  const installSkus = attachLabor?.installSkus?.[0] || {};
  const { serviceType, installSku } = installSkus || {};
  const { price: installRetailPrice } = attachLabor?.installSkus?.[0]?.installRetailPrice || {};

  const modelNumber = data?.product?.identifiers?.modelNumber || {};
  const { leadGenUrl } = data?.product?.details?.installation || {};

  const type = serviceType?.toLowerCase();
  const displayAttachLabor = attachLaborEligible({
    isInstallationSku,
    inStoreAssemblyAvailable,
    availableFulfillments,
    currentFulfillment: fulfillment
  });

  return (
    <section className="product-add-ons">

      {displayAttachLabor && (
        <AttachLabor
          channel={channel}
          onChange={onChange}
          itemId={installSku}
          type={type}
          price={installRetailPrice}
          installProcess={installProcess}
          priceIncludes={priceIncludes}
          fulfillment={fulfillment}
          availableFulfillments={availableFulfillments}
          isInstallationSku={isInstallationSku}
          inStoreAssemblyAvailable={inStoreAssemblyAvailable}
        />
      )}

      {isDualPath && (
        <DualPath
          channel={channel}
          onChange={onChange}
          itemId={itemId}
          category={installationProgramCategory}
          serviceUrl={leadGenUrl}
        />
      )}

      {isSAMDualPath && (
        <SAMDualPath
          channel={channel}
          onChange={onChange}
          itemId={itemId}
          modelNumber={modelNumber}
        />
      )}

      {(!displayAttachLabor && (isProReferral || isAssembly)) && (
        <ProReferral
          onChange={onChange}
          channel={channel}
          category={getCategory(serviceName)}
          isAssembly={isAssembly}
        />
      )}

      {isProtectionPlan && (
        <HomeDepotProtectionPlan
          channel={channel}
          onChange={onChange}
          itemId={protectionPlanItemId}
          price={price}
          term={termLength}
          url={detailsUrl}
        />
      )}
    </section>
  );
};

ProductAddOns.displayName = 'ProductAddOns';

ProductAddOns.defaultProps = {
  availableFulfillments: [],
  fulfillment: null
};
ProductAddOns.propTypes = {
  /** Product Number */
  itemId: string.isRequired,
  /** Function to call when checkbox is clicked */
  onChange: func.isRequired,
  /** Fulfillment method selected */
  fulfillment: string,
  /** List of currently available fulfillment types  */
  availableFulfillments: arrayOf(string)
};

ProductAddOns.dataModel = dataModel;

export { ProductAddOns };
